import * as React from 'react';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { Icon } from 'ui/common/icon';
import { FactIconList, FactBadgeList } from 'app/area/case/components/fact-box/renderers';
import {sectionFor} from './section';
import { MdFlashOn, MdCheckBox, MdFormatListNumbered, MdAttachFile, MdGroup } from 'react-icons/md';
import { FactBoxProps } from 'app/area/case/components/fact-box/types';
import { tableName } from 'app/area/case/case-data-model';

export const IconsConsequences = (props:FactBoxProps) => {
  const defaultIconConsequences = <MdFlashOn />;
  return <FactIconList title={props.titleConsequences} iconLine={true} icons={props.childValueItems.ActConsConsequenceIcons}
    defaultIcon={defaultIconConsequences} section={sectionFor(props, tableName.consequences)} url={props.url} />;
};

export const IconBowtie = (props: FactBoxProps) => {
  const iconBowtieAbsolutePath = toAbsoluteUrl('i/bowtie.svg');
  return <FactIconList title={props.titleBowtie} iconLine={true} url={`/bowtie/${props.caseId}`} 
    icons={[{description: props.tooltipBowtie, icon: iconBowtieAbsolutePath, value: 0, propName: 'bowtie'}] as any}/>;
};

export const BadgeListActions = (props: FactBoxProps) => {
  const {
    numberOfClosedActions,
    tooltipNumberOfActions,
    tooltipClosedActions,
    titleActions,
    count
  } = props;
  const iconActions = <MdFormatListNumbered />;
  const items = [
    { value: count as number, tooltip: tooltipNumberOfActions },
    {
      value: numberOfClosedActions.count as number,
      tooltip: tooltipClosedActions,
      classNames: numberOfClosedActions.propName
    }
  ];

  return <FactBadgeList icon={iconActions} title={titleActions} valueItems={items} classNames={'action-badge'}
          section={sectionFor(props, tableName.action)} factProps={props} />;
};

export const BadgeListAttachments = (props: FactBoxProps) => {
  const { tooltipNumberOfAttachments, titleAttachments, count } = props;
  const iconAttachments = <MdAttachFile />;
  const items = [
    { value: count as number, tooltip: tooltipNumberOfAttachments }
  ];
  return <FactBadgeList icon={iconAttachments} title={titleAttachments} valueItems={items}
          section={sectionFor(props, tableName.attachmentType)} factProps={props} />;
};

export const BadgeListConnectedCases = (
  props: FactBoxProps
) => {
  const { tooltipNumberOfConnectedCases, titleConnectedCases, count } = props;
  const iconAbsolutePath = toAbsoluteUrl('i/connected-cases.svg');
  const iconConnectedCases = <Icon url={iconAbsolutePath} />;
  const items = [
    {
      value: count as number,
      tooltip: tooltipNumberOfConnectedCases
    }
  ];

  return <FactBadgeList icon={iconConnectedCases} title={titleConnectedCases} valueItems={items}
          section={sectionFor(props, tableName.connectedCases)} factProps={props} />;
};

export const BadgeListComments = (props: FactBoxProps) => {
  const { tooltipNumberOfComments, titleComments, count } = props;
  const iconAbsolutPath = toAbsoluteUrl('i/comments.svg');
  const iconComments = <Icon url={iconAbsolutPath} />;
  const items = [
    { value: count as number, tooltip: tooltipNumberOfComments }
  ];

  return <FactBadgeList icon={iconComments} title={titleComments} valueItems={items}
          section={sectionFor(props, tableName.comment)} factProps={props} />;
};

export const BadgeListCauses = (props: FactBoxProps) => {
  const { tooltipNumberOfCauses, titleCauses, count } = props;
  const iconAbsolutPath = toAbsoluteUrl('i/causes-icon.svg');
  const iconCauses = <Icon url={iconAbsolutPath} />;
  const items = [
    { value: count as number, tooltip: tooltipNumberOfCauses }
  ];
  return <FactBadgeList icon={iconCauses} title={titleCauses} valueItems={items}
          section={sectionFor(props, tableName.causes)} factProps={props} />;
};

export const BadgeListInvolvedPersons = (
  props:FactBoxProps
) => {
  const { tooltipNumberOfInvolvedPersons, titleInvolvedPersons, count } = props;
  const iconInvolvedPersons = <MdGroup />;
  const items = [
    {
      value: count,
      tooltip: tooltipNumberOfInvolvedPersons
    }
  ];

  return <FactBadgeList icon={iconInvolvedPersons} title={titleInvolvedPersons} valueItems={items as any}
          section={sectionFor(props, tableName.involvedPerson)} factProps={props} />;
};

export const BadgeListInvolvedDrugs = (props: FactBoxProps) => {
  const { count } = props;
  const { tooltipNumberOfInvolvedDrugs, titleInvolvedDrugs } = props;
  const iconAbsolutePath = toAbsoluteUrl('i/drug-icon.svg');
  const iconInvolvedDrugs = <Icon url={iconAbsolutePath} />;
  const items = [
    {
      value: count,
      tooltip: tooltipNumberOfInvolvedDrugs
    }
  ];

  return <FactBadgeList icon={iconInvolvedDrugs} title={titleInvolvedDrugs} valueItems={items as any}
          section={sectionFor(props, tableName.involvedDrug)} factProps={props} />;
};

export const BadgeListIntegrityStatus = (props: FactBoxProps) => {
  const { titleIntegrityStatus, integrityStatus, integrityScore } = props;
  const iconIntegrityStatus = integrityStatus && (
    <Icon url={integrityStatus.icon} />
  );
  const score = integrityScore ? integrityScore as number: undefined;
  const tooltipIntegrityStatus = integrityStatus && integrityStatus.description;
  const items = [{ value: score, tooltip: tooltipIntegrityStatus }];

  return <FactBadgeList icon={iconIntegrityStatus} title={titleIntegrityStatus} valueItems={items}
          useNestedIndicator={false} section={sectionFor(props, 'IntegrityStatus')}
          factProps={props}/>;
};

export const BadgeListIntegrityStatusBarrierEval = (props: FactBoxProps) => {
  const { titleIntegrityStatusBarrierEval, integrityStatusBarrierEval } = props;
  const iconIntegrityStatus = integrityStatusBarrierEval && (
    <Icon url={integrityStatusBarrierEval.icon} />
  );
  const tooltipIntegrityStatusBarrrierEval =
    integrityStatusBarrierEval && integrityStatusBarrierEval.description;
  const items = [
    { value: undefined, tooltip: tooltipIntegrityStatusBarrrierEval }
  ];
  return <FactBadgeList icon={iconIntegrityStatus} title={titleIntegrityStatusBarrierEval} valueItems={items}
          useNestedIndicator={false} section={sectionFor(props, 'BarrierEval')} factProps={props} />;
};

export const BadgeListChecklists = (props: FactBoxProps) => {
  const {
    titleChecklists,
    checklistEvaluations,
    count,
    tooltipNumberOfChecklistItems,
    visibleForValues
  } = props;
  const icon = <MdCheckBox />;
  const splittedVisibleForValues = !!visibleForValues && String(visibleForValues)
    .split(',')
    .map(e => Number(e));
  const filteredChecklistEvaluations = !!splittedVisibleForValues && !!splittedVisibleForValues.length &&
    checklistEvaluations.filter(r => splittedVisibleForValues.indexOf(r.key) > -1
  );
  const totalChecklistItems = [
    {
      value: count,
      tooltip: tooltipNumberOfChecklistItems
    }
  ];
  const resultingItems = [
    ...totalChecklistItems,
    ...filteredChecklistEvaluations
  ];

  return <FactBadgeList icon={icon} title={titleChecklists} valueItems={resultingItems}
          useNestedIndicator={true} section={sectionFor(props, tableName.checklist)}
          factProps={props}/>;
};

export const BadgeListAssessments = (props: FactBoxProps) => {
  const { tooltipNumberOfAssessments, titleAssessments } = props;
  const iconAbsolutePath = toAbsoluteUrl('i/Assessment.svg');
  const iconAssessments = <Icon url={iconAbsolutePath} />;
  const items = [
    { value: props.count as number, tooltip: tooltipNumberOfAssessments }
  ];
  return <FactBadgeList icon={iconAssessments} title={titleAssessments} valueItems={items}
          section={sectionFor(props, tableName.assessment)}
          factProps={props} />;
};
